/**
 * CairnSkillEffect — Earth tree sculptor fork 2 active (cairn sub-path).
 *
 * Hurl a boulder at a target point within 5 tiles. 20 damage on direct impact,
 * 10 AoE splash in a 1-tile radius around the landing point. The boulder
 * persists as a permanent 1×1 terrain obstacle (40 HP, blocks movement and
 * projectiles) with a rough terrain zone (60% speed, 1-tile radius) until
 * destroyed. When the boulder reaches 0 HP, the associated zone is removed
 * via onBoulderDestroyed().
 *
 * Upgrade keys on scope 'cairn':
 * - rangePerFormation: additive (Crowned: +0.5 per formation within 5 tiles)
 * - maxBonusRangeTiles: max (Crowned: cap at 2.5)
 * - formationDetonationDamage: additive (Landslide: 10)
 * - detonationRadiusTiles: additive (Landslide: 1.5)
 * - cooldownReductionPerPiece: additive (Quarried: 0.08)
 * - maxCooldownReduction: max (Quarried: 0.32)
 *
 * Extends InstantSkillEffect; overrides cooldown and update for timer tracking.
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see earth-spec.md § Sculptor Path — Cairn Sub-Path
 */

import { InstantSkillEffect } from '../../InstantSkillEffect.js';

// ---------------------------------------------------------------------------
// Terrain config interface (mirrors TerrainConfig subset for boulder)
// ---------------------------------------------------------------------------

export interface CairnTerrainConfig {
  readonly type: 'formation';
  readonly position: { x: number; y: number };
  readonly widthTiles: number;
  readonly heightTiles: number;
  readonly hp: number;
  readonly durationMs: number;
  readonly sourceSkillId: string;
  readonly blocksMovement: boolean;
  readonly blocksProjectiles: boolean;
}

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface CairnDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMaxEffect(skillId: string, key: string): number | undefined;
    hasEffect(skillId: string, key: string): boolean;
  };
  readonly targeting: {
    /** Returns the aimed target point clamped to the given range, plus footprint. */
    getTargetPoint(rangeTiles: number): {
      position: { x: number; y: number };
      footprintTiles: Array<{ x: number; y: number }>;
    };
    /** Returns enemy IDs overlapping the exact landing tile. */
    getEnemiesAtPosition(position: { x: number; y: number }): string[];
    /** Returns enemy IDs within the given pixel radius of a world position. */
    getEnemiesInRadius(
      center: { x: number; y: number },
      radiusPx: number,
    ): string[];
    /** Returns the player's current world position. */
    getPlayerPosition(): { x: number; y: number };
  };
  readonly terrain: {
    place(config: CairnTerrainConfig): string;
    remove(terrainId: string): void;
    /** Count of active formations within a tile-radius of a position (Crowned). */
    getActiveCountInRadius(
      type: string,
      x: number,
      y: number,
      radiusTiles: number,
    ): number;
    /** Formations within a tile-radius of a position (Landslide). */
    getFormationsInRadius(
      x: number,
      y: number,
      radiusTiles: number,
    ): Array<{ id: string; position: { x: number; y: number } }>;
    /** Total active terrain piece count across all types (Quarried). */
    getActiveCount(): number;
  };
  readonly zones: {
    create(config: {
      type: string;
      position: { x: number; y: number };
      radiusTiles: number;
      durationMs: number;
      speedMultiplier: number;
      sourceSkillId: string;
    }): string;
    remove(zoneId: string): void;
  };
  readonly pathfinding: {
    markImpassable(positions: Array<{ x: number; y: number }>): void;
  };
  readonly combat: {
    applyDamage(
      targetId: string,
      damage: number,
      opts: { sourceSkillId: string },
    ): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface CairnConfig {
  /** Direct impact damage to enemies at the landing tile. */
  readonly baseDamage: number;
  /** AoE splash damage to enemies in the radius (excludes direct-hit targets). */
  readonly aoeDamage: number;
  /** AoE splash radius in tiles. */
  readonly aoeRadiusTiles: number;
  /** Base throw range in tiles (before Crowned bonus). */
  readonly baseRangeTiles: number;
  /** Long cooldown tier in ms (before Quarried reduction). */
  readonly cooldownMs: number;
  /** Boulder HP. */
  readonly boulderHp: number;
  /** Boulder footprint width in tiles. */
  readonly boulderWidthTiles: number;
  /** Boulder footprint height in tiles. */
  readonly boulderHeightTiles: number;
  /** Rough terrain zone radius around the boulder in tiles. */
  readonly roughTerrainRadiusTiles: number;
  /** Speed multiplier inside the rough terrain zone (0.6 = 60% speed). */
  readonly roughTerrainSpeedMultiplier: number;
  /** Pixels per tile — converts tile distances to pixels for AoE radius. */
  readonly tileSizePx: number;
  /** Crowned: detection radius in tiles for counting nearby formations. */
  readonly crownedDetectionRadiusTiles: number;
  /** Landslide: detection radius in tiles for formation proximity. */
  readonly landslideDetectionRadiusTiles: number;
  /** Landslide: rough terrain duration at detonation point in ms. */
  readonly landslideRoughTerrainDurationMs: number;
  /** Landslide: speed multiplier for detonation rough terrain. */
  readonly landslideRoughTerrainSpeedMultiplier: number;
}

const DEFAULT_CONFIG: CairnConfig = {
  baseDamage: 20,
  aoeDamage: 10,
  aoeRadiusTiles: 1,
  baseRangeTiles: 5,
  cooldownMs: 2000,                          // long cooldown tier
  boulderHp: 40,
  boulderWidthTiles: 1,
  boulderHeightTiles: 1,
  roughTerrainRadiusTiles: 1,
  roughTerrainSpeedMultiplier: 0.6,
  tileSizePx: 32,
  crownedDetectionRadiusTiles: 5,
  landslideDetectionRadiusTiles: 1.5,
  landslideRoughTerrainDurationMs: 3000,
  landslideRoughTerrainSpeedMultiplier: 0.75,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class CairnSkillEffect extends InstantSkillEffect {
  readonly skillId = 'cairn';

  private readonly deps: CairnDeps;
  private readonly config: CairnConfig;

  // Cooldown timer (ms remaining until ready)
  private _cooldownTimer = 0;

  // Boulder → zone mapping: when a boulder is destroyed, its zone is removed
  private _boulderZoneMap = new Map<string, string>();

  // Telemetry
  private _bouldersPlacedCount = 0;
  private _directHitCount = 0;
  private _aoeHitCount = 0;
  private _zonesCreatedCount = 0;
  private _landslideDetonationCount = 0;
  private _lastEffectiveRange = 0;
  private _pathfindingCallCount = 0;
  private _lastTerrainConfig: CairnTerrainConfig | null = null;
  private _lastZoneConfig: {
    type: string;
    position: { x: number; y: number };
    radiusTiles: number;
    durationMs: number;
    speedMultiplier: number;
    sourceSkillId: string;
  } | null = null;
  private _lastLandingPosition: { x: number; y: number } | null = null;

  constructor(deps: CairnDeps, config: Partial<CairnConfig> = {}) {
    super();
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  override get cooldown(): number { return this._cooldownTimer; }

  get bouldersPlacedCount(): number { return this._bouldersPlacedCount; }
  get directHitCount(): number { return this._directHitCount; }
  get aoeHitCount(): number { return this._aoeHitCount; }
  get zonesCreatedCount(): number { return this._zonesCreatedCount; }
  get landslideDetonationCount(): number { return this._landslideDetonationCount; }
  get lastEffectiveRange(): number { return this._lastEffectiveRange; }
  get pathfindingCallCount(): number { return this._pathfindingCallCount; }
  get lastTerrainConfig(): CairnTerrainConfig | null { return this._lastTerrainConfig; }
  get lastZoneConfig(): typeof this._lastZoneConfig { return this._lastZoneConfig; }
  get lastLandingPosition(): { x: number; y: number } | null { return this._lastLandingPosition; }

  /** Number of currently tracked boulders (each paired with a zone). */
  get activeBoulderCount(): number { return this._boulderZoneMap.size; }

  // --- InstantSkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownTimer <= 0;
  }

  activate(): boolean {
    if (this._cooldownTimer > 0) return false;

    // --- Crowned: dynamic range bonus based on nearby formations ---
    const rangePerFormation =
      this.deps.resolver.getAdditiveEffect('cairn', 'rangePerFormation');
    const maxBonusRange =
      this.deps.resolver.getMaxEffect('cairn', 'maxBonusRangeTiles') ?? 0;

    let rangeBonus = 0;
    if (rangePerFormation > 0 && maxBonusRange > 0) {
      const playerPos = this.deps.targeting.getPlayerPosition();
      const nearbyCount = this.deps.terrain.getActiveCountInRadius(
        'formation',
        playerPos.x,
        playerPos.y,
        this.config.crownedDetectionRadiusTiles,
      );
      rangeBonus = Math.min(nearbyCount * rangePerFormation, maxBonusRange);
    }

    const effectiveRange = this.config.baseRangeTiles + rangeBonus;
    this._lastEffectiveRange = effectiveRange;

    // Get target point from game layer
    const { position, footprintTiles } =
      this.deps.targeting.getTargetPoint(effectiveRange);
    this._lastLandingPosition = position;

    // --- Direct impact damage (enemies at the landing tile) ---
    const directTargets =
      this.deps.targeting.getEnemiesAtPosition(position);
    for (const enemyId of directTargets) {
      this.deps.combat.applyDamage(enemyId, this.config.baseDamage, {
        sourceSkillId: 'cairn',
      });
      this._directHitCount++;
    }

    // --- AoE splash damage (excludes direct-hit targets) ---
    const aoeRadiusPx = this.config.aoeRadiusTiles * this.config.tileSizePx;
    const aoeTargets =
      this.deps.targeting.getEnemiesInRadius(position, aoeRadiusPx);
    const directSet = new Set(directTargets);
    for (const enemyId of aoeTargets) {
      if (!directSet.has(enemyId)) {
        this.deps.combat.applyDamage(enemyId, this.config.aoeDamage, {
          sourceSkillId: 'cairn',
        });
        this._aoeHitCount++;
      }
    }

    // --- Landslide: detonate nearby formations ---
    const hasLandslide =
      this.deps.resolver.hasEffect('cairn', 'formationDetonationDamage');
    if (hasLandslide) {
      const detonationDamage =
        this.deps.resolver.getAdditiveEffect('cairn', 'formationDetonationDamage');
      const detonationRadius =
        this.deps.resolver.getAdditiveEffect('cairn', 'detonationRadiusTiles');

      const nearbyFormations = this.deps.terrain.getFormationsInRadius(
        position.x,
        position.y,
        this.config.landslideDetectionRadiusTiles,
      );

      for (const formation of nearbyFormations) {
        // Remove the formation
        this.deps.terrain.remove(formation.id);

        // Remove its associated zone if tracked
        const associatedZone = this._boulderZoneMap.get(formation.id);
        if (associatedZone) {
          this.deps.zones.remove(associatedZone);
          this._boulderZoneMap.delete(formation.id);
        }

        // AoE damage at detonation point
        const detRadiusPx = detonationRadius * this.config.tileSizePx;
        const detTargets = this.deps.targeting.getEnemiesInRadius(
          formation.position,
          detRadiusPx,
        );
        for (const enemyId of detTargets) {
          this.deps.combat.applyDamage(enemyId, detonationDamage, {
            sourceSkillId: 'cairn',
          });
        }

        // Rough terrain at detonation point (timed, not permanent)
        this.deps.zones.create({
          type: 'rough_terrain',
          position: formation.position,
          radiusTiles: detonationRadius,
          durationMs: this.config.landslideRoughTerrainDurationMs,
          speedMultiplier: this.config.landslideRoughTerrainSpeedMultiplier,
          sourceSkillId: 'cairn',
        });

        this._landslideDetonationCount++;
      }
    }

    // --- Place boulder terrain (permanent until destroyed) ---
    const terrainConfig: CairnTerrainConfig = {
      type: 'formation',
      position,
      widthTiles: this.config.boulderWidthTiles,
      heightTiles: this.config.boulderHeightTiles,
      hp: this.config.boulderHp,
      durationMs: 0,          // permanent until HP depleted
      sourceSkillId: 'cairn',
      blocksMovement: true,
      blocksProjectiles: true,
    };

    const terrainId = this.deps.terrain.place(terrainConfig);
    this._lastTerrainConfig = terrainConfig;
    this._bouldersPlacedCount++;

    // Synchronous pathfinding update
    this.deps.pathfinding.markImpassable(footprintTiles);
    this._pathfindingCallCount++;

    // --- Create rough terrain zone (permanent, tied to boulder lifetime) ---
    const zoneConfig = {
      type: 'rough_terrain' as const,
      position,
      radiusTiles: this.config.roughTerrainRadiusTiles,
      durationMs: 0,          // permanent while boulder alive
      speedMultiplier: this.config.roughTerrainSpeedMultiplier,
      sourceSkillId: 'cairn' as const,
    };

    const zoneId = this.deps.zones.create(zoneConfig);
    this._lastZoneConfig = zoneConfig;
    this._zonesCreatedCount++;

    // Track boulder → zone pairing for destruction callback
    this._boulderZoneMap.set(terrainId, zoneId);

    // --- Quarried: dynamic cooldown reduction based on active terrain pieces ---
    const reductionPerPiece =
      this.deps.resolver.getAdditiveEffect('cairn', 'cooldownReductionPerPiece');
    const maxReduction =
      this.deps.resolver.getMaxEffect('cairn', 'maxCooldownReduction') ?? 0;

    let cooldownMultiplier = 1;
    if (reductionPerPiece > 0 && maxReduction > 0) {
      const activePieces = this.deps.terrain.getActiveCount();
      const reduction = Math.min(activePieces * reductionPerPiece, maxReduction);
      cooldownMultiplier = 1 - reduction;
    }

    // Start cooldown
    this._cooldownTimer = this.config.cooldownMs * cooldownMultiplier;

    return true;
  }

  /**
   * Called by TerrainConstructionSystem when a boulder reaches 0 HP.
   * Removes the associated rough terrain zone.
   */
  onBoulderDestroyed(terrainId: string): void {
    const zoneId = this._boulderZoneMap.get(terrainId);
    if (zoneId) {
      this.deps.zones.remove(zoneId);
      this._boulderZoneMap.delete(terrainId);
    }
  }

  override update(delta: number): void {
    if (this._cooldownTimer > 0) {
      this._cooldownTimer -= delta;
      if (this._cooldownTimer < 0) this._cooldownTimer = 0;
    }
  }
}
